/**
 * Extract a brand SEED (a single vivid hex) from an uploaded logo, client-side.
 * Draws the image to a tiny canvas and picks the most saturated, reasonably-light
 * pixel — a decent stand-in for "the brand colour". Best-effort: resolves null if
 * the image can't be read (the caller falls back to a default seed).
 *
 * Kept out of the command layer so `generateCandidates` stays pure/headless — the
 * UI resolves the seed here, then passes `{ kind: "logo", seed }`.
 */
export async function seedFromLogoFile(file: File): Promise<string | null> {
  try {
    const bitmap = await createImageBitmap(file);
    const W = 48;
    const H = Math.max(1, Math.round((bitmap.height / bitmap.width) * W));
    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(bitmap, 0, 0, W, H);
    const { data } = ctx.getImageData(0, 0, W, H);

    let best = { score: -1, r: 124, g: 58, b: 237 };
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i]!, g = data[i + 1]!, b = data[i + 2]!, a = data[i + 3]!;
      if (a < 128) continue; // skip transparent
      const max = Math.max(r, g, b), min = Math.min(r, g, b);
      const sat = max === 0 ? 0 : (max - min) / max;
      const light = max / 255;
      // Favour saturated, mid-to-light pixels; ignore near-white/near-black.
      if (light < 0.12 || light > 0.97) continue;
      const score = sat * (0.5 + 0.5 * light);
      if (score > best.score) best = { score, r, g, b };
    }
    const hx = (n: number) => n.toString(16).padStart(2, "0");
    return `#${hx(best.r)}${hx(best.g)}${hx(best.b)}`;
  } catch {
    return null;
  }
}
